import React from 'react';
import { normalizeFeatureValue } from '../../utils/featureNormalization';
import { sortFeatureValues } from '../../utils/sortingUtils';

/**
 * MultiCheckboxInput - Multiple checkbox input (for multi-select boolean groups)
 * 
 * @param {Object} props
 * @param {Object} props.feature - Feature object from RuleEngine
 * @param {Array} props.value - Array of selected values
 * @param {Function} props.onChange - Change handler
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.className - Additional CSS classes
 */
const MultiCheckboxInput = ({ feature, value, onChange, disabled = false, isOptionDisabled, className = '' }) => {
    const values = sortFeatureValues((feature?.values || []).map(normalizeFeatureValue));
    const currentValues = Array.isArray(value)
        ? value.map(v => String(v))
        : (value === undefined || value === null || value === '' ? [] : [String(value)]);
    
    const handleChange = (selectedValue, isChecked) => {
        let newValues;
        if (isChecked) {
            newValues = [...currentValues, selectedValue];
        } else {
            newValues = currentValues.filter(v => v !== selectedValue);
        }
        onChange({ target: { value: newValues } });
    };
    
    if (values.length === 0) {
        return (
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                No options available
            </div>
        );
    }
    
    return (
        <div className="multi-checkbox-input-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {values.map((val) => {
                const valKey = val.value;
                const display = val.displayText || val.value;
                const isChecked = currentValues.includes(valKey);
                const isValueDisabled = disabled || Boolean(isOptionDisabled?.(valKey));
                
                return (
                    <label 
                        key={val.id || valKey}
                        className={className}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: isValueDisabled ? 'not-allowed' : 'pointer',
                            opacity: isValueDisabled ? 0.6 : 1,
                            backgroundColor: isChecked ? 'rgba(0, 30, 98, 0.05)' : undefined,
                            transition: 'background-color 0.15s'
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => !isValueDisabled && handleChange(valKey, e.target.checked)}
                            disabled={isValueDisabled}
                            style={{
                                width: '16px',
                                height: '16px',
                                accentColor: 'var(--accent)',
                                cursor: isValueDisabled ? 'not-allowed' : 'pointer'
                            }}
                        />
                        <span style={{ fontSize: '0.85rem' }}>{display}</span>
                        {val.description && (
                            <span 
                                style={{
                                    fontSize: '0.7rem',
                                    color: 'var(--text-secondary)',
                                    fontStyle: 'italic'
                                }}
                            >
                                - {val.description}
                            </span>
                        )}
                    </label>
                );
            })}
            
            {currentValues.length > 0 && (
                <div style={{
                    marginTop: '8px',
                    padding: '6px 10px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)'
                }}>
                    Selected: {currentValues.join(', ')}
                </div>
            )}
        </div>
    );
};

export default MultiCheckboxInput;
